import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { setAuthState, setUserRole } from "./features/auth/authSlice";
import { fetchUserProfile, selectUserStatus } from "./features/user/userSlice";
import { AppRoutes } from "./routes/AppRoutes";
import NavBar from "./components/Super Admin/NavBar/NavBar";
import { apiScopes } from "./config/msalConfig";
import { RootState } from "./app/store";
import api from "./api/axiosConfig";

const MainApp = () => {
  const dispatch = useAppDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const userStatus = useAppSelector(selectUserStatus);

  const role = useAppSelector((state: RootState) => state.auth.role);
  const navigate = useNavigate();

  // Sync MSAL state with Redux
  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response && response.account) {
          dispatch(
            setAuthState({ isAuthenticated: true, account: response.account }),
          );
        }
      })
      .catch((error) => {
        console.error("Redirect promise error:", error);
      });

    if (accounts.length > 0 && inProgress === "none") {
      dispatch(setAuthState({ isAuthenticated: true, account: accounts[0] }));
    }
  }, [dispatch, instance, accounts, inProgress]);

  // Fetch user profile
  useEffect(() => {
    if (isAuthenticated && userStatus === "idle") {
      dispatch(fetchUserProfile());
    }
  }, [isAuthenticated, userStatus, dispatch]);

  useEffect(() => {
    if (isAuthenticated && role === "Super Admin") {
      navigate("/forms");
    }
  }, [isAuthenticated, role, navigate]);

  useEffect(() => {
    const getAccessToken = async () => {
      if (accounts.length === 0) return;

      try {
        const response = await instance.acquireTokenSilent({
          ...apiScopes,
          account: accounts[0],
        });

        const accessToken = response.accessToken;
        console.log("Access Token:", accessToken); // 🔐 Use this in Postman

        sessionStorage.setItem("accessToken", accessToken);

        // ✅ Axios API call instead of fetch
        const backendResponse = await api.auth_api.get("/users/currentUser", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        //         const roleKey =
        //   backendResponse.data?.roleAssignments?.[0]?.roleId?.key ?? null;

        // if (roleKey) {
        //   dispatch(setUserRole(roleKey));
        // }

        console.log("Backend response:", backendResponse.data);

        sessionStorage.setItem(
          "backendResponse",
          JSON.stringify(backendResponse.data),
        );

        // const entraObjectId = backendResponse.data?.entraObjectId;

        const entraObjectId = backendResponse.data?.entraObjectId;
        if (entraObjectId) {
          const currentDate = new Date().toISOString().split("T")[0];
          try {
            const configResponse = await api.center_api.get(
              `/api/v1/center/users/${entraObjectId}/availability?startDate=${currentDate}&endDate=${currentDate}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            );
            sessionStorage.setItem(
              "configResponse",
              JSON.stringify(configResponse.data),
            );
            const centerId = configResponse.data?.data?.[0]?.centerId;

            if (centerId) {
              try {
                const centerConfigResponse = await api.center_api.get(
                  `/api/v1/center/${centerId}/config`,
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${accessToken}`,
                    },
                  },
                );

                const centerConfig = centerConfigResponse.data?.data;

                const assignedStation = centerConfig?.stations?.find(
                  (station: any) =>
                    station.users?.some(
                      (user: any) => user.userId === entraObjectId,
                    ),
                );

                // const stationName = assignedStation?.stationName;

                // if (stationName) {
                //   dispatch(setUserRole(stationName));
                // }

                const stationName = assignedStation?.stationName;

                if (stationName) {
                  sessionStorage.setItem("assignedStation", stationName);

                  dispatch(setUserRole(stationName));
                }
              } catch (error) {
                console.error("Error resolving station assignment:", error);
              }
            }
          } catch (error) {
            console.error("Error fetching user availability:", error);
          }
        }
      } catch (error) {
        console.error("Error acquiring access token:", error);
      }
    };

    if (isAuthenticated) {
      getAccessToken();
    }
  }, [isAuthenticated, accounts, instance]);

  return (
    <div>
      <AppRoutes />
    </div>
  );
};

export default MainApp;
